import get from 'lodash/get';

import ActiveCallConverter from 'scripts/application/dto_converters/active_call_converter';
import AgentVoiceCapabilitiesChecker from 'actions/configuration/lib/agent_voice_capabilities_checker';
import analytics from 'scripts/lib/analytics';
import Assignee from 'models/assignee';
import {
  checkAndResetNavigatingToNext,
  preloadCustomer,
  removeEmptyQueueNotifications,
} from 'actions/conversation/lib/conversation_workflow';
import cleanupNonCurrentCustomer from 'actions/customer/lib/cleanup_non_current_customer';
import ErrorReporter from 'scripts/infrastructure/error_reporter';
import hasLiveConnection from 'actions/phone_call/lib/has_live_connection';
import navigateToConversation from 'actions/conversation/lib/navigate_to_conversation';
import NotificationConverter from 'scripts/application/dto_converters/notification_converter';
import RoutingEventType from 'models/routing_event/routing_event_type';
import ShowConversationSystemNotification from 'actions/notification/show_conversation_system_notification';
import SystemNotificationGenerator from 'scripts/domain/contracts/system_notification_generator';
import { UnofferedCallMessages } from 'models/routing_event/unoffered_call_type';
import VerifyNewCustomer from 'actions/customer_match/verify_new_customer';

export default class PhoneCallRoutingEventObserver {
  constructor(context) {
    this.context = context;
    this.agentVoiceCapabilitiesChecker = new AgentVoiceCapabilitiesChecker(this.context);
  }

  handleRoutingEvent(event) {
    switch (event.type) {
      case RoutingEventType.OFFERED_CALL:
      case RoutingEventType.OFFERED_WARM_TRANSFER:
        this.handleOfferedCallEvent(event);
        break;
      case RoutingEventType.UNOFFERED_CALL:
        this.handleUnofferedCallEvent(event);
        break;
      case RoutingEventType.UNOFFERED_WARM_TRANSFER:
        this.handleUnofferedWarmTransferEvent(event);
        break;
      default:
    }
  }

  handleOfferedCallEvent(eventDto) {
    let activeCall = ActiveCallConverter.fromDto({
      customer: eventDto.profile,
      conversationId: eventDto.conversationId,
      conversationItem: eventDto.conversationItem,
      routingGroupId: eventDto.routingGroupId,
    });
    let currentAgent = this.context.stores.currentAgent.get();

    // Track all offered call events
    this._trackOfferedCallEvent(currentAgent.id, activeCall, 'Offered Call Received');

    // Check for version. This is to handle race conditions with fetching active calls from the phone gateway observer and receiving the offered call routing event.
    let isFetchActiveCallWhenDialed = this.context.stores.appFeatures.get().isEnabled('fetchActiveCallWhenDialed');
    if (isFetchActiveCallWhenDialed) {
      const existingActiveCall = this.context.stores.activeCall.get();
      if (existingActiveCall) {
        const existingPhoneCallItem = existingActiveCall.conversationItem;
        if (
          existingPhoneCallItem.id === activeCall.conversationItem.id &&
          existingPhoneCallItem.version >= activeCall.conversationItem.version
        ) {
          // Skip handling of a stale active call
          return;
        }
      }
    }

    if (!this.agentVoiceCapabilitiesChecker.canHandlePhoneCalls()) {
      return;
    }

    let connectionStates = this.context.stores.connectionState.get();

    if (connectionStates.isTwilioDisconnected()) {
      // report error if agent is routed a call without the Twilio phone gateway setup
      ErrorReporter.reportError(new Error('offered call error'), {
        message: 'phone gateway is disconnected',
        extra: {
          connectionStates,
          phonePreference: currentAgent.getVoiceConfigurationPreference(),
        },
      });
    }

    // Do not enable incoming call notification without live phone connection
    let hasLivePhoneConnection = hasLiveConnection(this.context, { activeCall });

    activeCall.setHasLiveConnection(hasLivePhoneConnection);
    if (currentAgent.isConfiguredForInteractiveVoice() && hasLivePhoneConnection) {
      checkAndResetNavigatingToNext(this.context);
    }
    if (eventDto.initiator) {
      activeCall.setInitiator(new Assignee(eventDto.initiator));
    }
    this.context.stores.activeCall.set(activeCall);
    this.context.gateways.customerHistoryMerge.subscribe(activeCall.customer.id);
    this.context.gateways.conversationItem.request(activeCall.customer.id, activeCall.conversationItem.id);

    preloadCustomer(this.context, activeCall.customer.id);

    removeEmptyQueueNotifications(this.context);
    if (!currentAgent.isConfiguredForInteractiveVoice() && currentAgent.usesExternalHandset()) {
      activeCall.setAgentHasAcceptedCall();
      this.context.stores.activeCall.set(activeCall);

      if (!activeCall.customer.isVerified()) {
        this.context.executeAction(VerifyNewCustomer, {
          customerId: activeCall.customer.id,
        });
      }
      navigateToConversation(this.context, {
        customerId: activeCall.customer.id,
        conversationId: activeCall.conversationId,
      });
    }

    // Only show the system notification for live calls
    if (hasLivePhoneConnection) {
      let notification = NotificationConverter.fromDto(eventDto);
      this.context.executeAction(ShowConversationSystemNotification, notification);
    }

    // Track offered call events that are not ignored ignored due to missing permissions or capabilities
    this._trackOfferedCallEvent(currentAgent.id, activeCall, 'Offered Call Handled');
  }

  handleUnofferedCallEvent(eventDto) {
    let activeCall = this.context.stores.activeCall.get();
    const currentAgent = this.context.stores.currentAgent.get();
    let shouldDisplayUnofferedCallNotification = true;
    if (activeCall) {
      shouldDisplayUnofferedCallNotification = activeCall.shouldDisplayIncomingCallNotification(currentAgent.id);
    }

    this.cleanUpActiveCall(eventDto.conversationItem.id);
    SystemNotificationGenerator.closeNotification(eventDto.conversationItem.id);

    if (!this.agentVoiceCapabilitiesChecker.canHandlePhoneCalls()) {
      return;
    }

    if (UnofferedCallMessages[eventDto.reason] && shouldDisplayUnofferedCallNotification) {
      const notification = NotificationConverter.fromDto(eventDto);
      this.context.stores.notifications.addOrReplace(notification);
    }

    this.context.capacityManager.postponeCallRouting();
  }

  handleUnofferedWarmTransferEvent(eventDto) {
    let activeCall = this.context.stores.activeCall.get();
    const currentAgent = this.context.stores.currentAgent.get();
    let shouldDisplayUnofferedCallNotification = true;
    if (activeCall) {
      shouldDisplayUnofferedCallNotification = activeCall.shouldDisplayIncomingCallNotification(currentAgent.id);
    }
    this.cleanUpActiveCall(eventDto.conversationItem.id);
    SystemNotificationGenerator.closeNotification(eventDto.conversationItem.id);

    if (!this.agentVoiceCapabilitiesChecker.canHandlePhoneCalls()) {
      return;
    }
    if (shouldDisplayUnofferedCallNotification) {
      const notification = NotificationConverter.fromDto(eventDto);
      this.context.stores.notifications.addOrReplace(notification);
    }
  }

  cleanUpActiveCall(conversationItemId) {
    const activeCall = this.context.stores.activeCall.get();
    if (activeCall && activeCall.conversationItem.id === conversationItemId) {
      cleanupNonCurrentCustomer(this.context, activeCall.customer.id);
      this.removeActiveCall(activeCall);
    }
  }

  removeActiveCall(activeCall) {
    this.context.stores.activeCall.remove();
    this.context.gateways.activeCall.cancelFetches();
    this.context.gateways.customerHistoryMerge.unsubscribe(activeCall.customer.id);
    this.context.gateways.conversationItem.unsubscribe(activeCall.customer.id, activeCall.conversationItem.id);
  }

  // Track offered call events in Amplitude
  _trackOfferedCallEvent(agentId, activeCall, eventName) {
    let isOfferedCallLoggingEnabled = this.context.stores.appFeatures.get().isEnabled('offeredCallLogging');
    if (isOfferedCallLoggingEnabled) {
      let agentCallParticipant = activeCall.conversationItem.content.findParticipantById(agentId);
      let callId = agentCallParticipant && agentCallParticipant.callId;
      analytics.track(eventName, {
        callId,
        conversationItemId: get(activeCall, 'conversationItem.id', ''),
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/scripts/application/actions/phone_call/phone_call_routing_event_observer.js